import { useMemo, useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import Table from '@shared/components/table/Table';
import { ColumnDef } from '@tanstack/react-table';
import { useStore } from '@shared/hooks/useStore';
import PlanDetailModal from './PlanDetailModal';
import Pagination from '@shared/components/pagination/Pagination';
import { PACKAGE_TYPE } from '@shared/types/enums';
import { Package } from '@shared/types/package';

export default observer(function PlanManagementPage() {
    // hooks
    const { groupAdminStore, modalStore: { showModal, hideModal } } = useStore();

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    const fetchPlans = (page: number) => {
        const params = {
            page,
            size: pageSize
        };
        groupAdminStore.fetchPackages(params);
    };

    useEffect(() => {
        fetchPlans(currentPage);
    }, [currentPage]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleShowPlanDetail = (plan: Package) => {
        showModal({
            id: 'modal-plan-detail',
            size: '3xl',
            showHeader: false,
            footerClassName: 'hidden',
            contentClassName: '!p-0 overflow-hidden w-[95vw] sm:rounded-[8px] [&>button]:hidden max-sm:w-full max-sm:h-full max-sm:max-w-none max-sm:m-0 max-sm:top-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none',
            content: (
                <PlanDetailModal
                    plan={plan}
                    onClose={() => hideModal('modal-plan-detail')}
                />
            )
        });
    };

    const columns: ColumnDef<Package>[] = useMemo(() => [
        {
            accessorKey: 'no',
            header: 'No.',
            cell: ({ row }) => (currentPage - 1) * pageSize + row.index + 1,
            size: 60,
            enableSorting: false
        },
        {
            accessorKey: 'code',
            header: 'プランコード',
            enableSorting: false
        },
        {
            accessorKey: 'name',
            header: 'プラン名',
            cell: ({ row }) => (
                <span
                    className='text-[#0095D9] cursor-pointer hover:underline'
                    onClick={() => handleShowPlanDetail(row.original)}
                >
                    {row.original.name || '--'}
                </span>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'packageType',
            header: 'プラン種別',
            cell: ({ row }) => {
                const type = row.original.packageType;
                if (type === PACKAGE_TYPE.UNLIMITED) {
                    return '読み放題プラン';
                }
                if (type === PACKAGE_TYPE.SPECIFIED) {
                    return '購入冊数プラン';
                }
                return '--';
            },
            enableSorting: false
        },
        {
            accessorKey: 'numberPublications',
            header: '指定書籍・雑誌数',
            cell: ({ row }) => {
                if (row.original.packageType === PACKAGE_TYPE.UNLIMITED) {
                    return '--';
                }
                return row.original.numberPublications - row.original.maxSelectCount || '--';
            },
            enableSorting: false
        },
        {
            accessorKey: 'maxSelectCount',
            header: '選択可能冊数',
            cell: ({ row }) => {
                if (row.original.packageType === PACKAGE_TYPE.UNLIMITED) {
                    return '--';
                }
                return row.original.maxSelectCount || '--';
            },
            enableSorting: false
        },
        {
            accessorKey: 'numberUsed',
            header: '利用中のユーザー数',
            enableSorting: false
        }
    ], [currentPage]);

    return (
        <section className='py-6'>
            {/* Page Title */}
            <h1 className='text-[30px] leading-[36px] font-semibold mb-10'>
                プラン管理
            </h1>

            {/* Data Table */}
            <div className='bg-white rounded-[10px] shadow-[0px_0px_14px_0px_#00000008] p-4 md:p-6'>
                <Table
                    columns={columns}
                    data={groupAdminStore.packages}
                    showTableFooter={false}
                    showSelectPageSize={false}
                    noData={<p className='text-[#999999]'>該当するデータが見つかりませんでした。</p>}
                    headerClassName='bg-[#F2F2F2] text-[#6F767E] font-medium'
                />

                <div className='flex justify-center py-4 border-t border-gray-200 mt-4'>
                    <Pagination
                        type='table-pagination'
                        paginationClassName='w-fit'
                        currentPage={currentPage}
                        totalRecords={groupAdminStore.totalPackages}
                        pageSize={pageSize}
                        onPageChange={handlePageChange}
                    />
                </div>
            </div>
        </section>
    );
});
